import * as api from "./adapter";
import { normalizeRelations } from "./convertors/normalizeRelations";
import { uploadAttachment } from "./userAttachments";
import downloadFile from "./downloadFile";

const updateTerms = async ({ id, terms, levels, contract_terms }) => {
  if (terms)
    await api.update({ id, action: "update_terms", type: "owners", ...terms });
  if (levels) {
    const levelsData = Object.keys(levels).map((k) => ({
      level_id: k.split("_")[1],
      fee: levels[k],
    }));
    await api.update({
      id,
      action: "update_level_terms",
      type: "owners",
      data: levelsData,
    });
  }
  if (contract_terms) {
    // QA 2026-05-26: the JSON:API response for owner #N includes a
    // nested `contract_terms` object that carries BE-side metadata
    // (`id`, `owner_id`, `created_at`, `updated_at`) alongside the
    // three editable duration fields. Naïvely spreading the whole
    // object into the PATCH overrode our outer `id: <ownerId>` with
    // `id: <contractTermsId>`, so the URL became
    // `/api/owners/48/update_contract_terms` instead of
    // `/api/owners/1/update_contract_terms` — the BE then failed
    // silently and the duration save was lost. Strip the metadata
    // before spreading so only the editable fields go on the wire.
    const {
      id: _contractTermsId,
      owner_id: _ownerId,
      created_at: _createdAt,
      updated_at: _updatedAt,
      ...durationFields
    } = contract_terms;
    await api.update({
      id,
      action: "update_contract_terms",
      type: "owners",
      ...durationFields,
    });
  }
};

export default {
  /**
   * getOwners
   * @returns {Promise<*>}
   */
  index: async ({ pagination, sort, search }) => {
    const data = await api.read("owners", {
      query: {
        include: "contract_template,document_templates",
        sort: `${sort.direction === "asc" ? "" : "-"}${sort.field}`,
        page: { size: pagination.itemsPerPage, number: pagination.current },
        filter: { title: search },
      },
    });
    return normalizeRelations(data);
  },
  /**
   *
   * @param id
   * @returns {Promise}
   */
  show: (id) => api.read(`owners/${id}`),
  /**
   * createOwner
   * @param attributes
   * @returns {Promise}
   */
  create: async (attributes) => {
    const {
      terms,
      levels,
      contract_template,
      current_user_id,
      // QA bug 3.3 follow-up. When the user creates an Owner
      // without attaching any files (the common path — the form's
      // dropzone is optional) the form payload omits the key, so
      // destructuring left `document_templates` as `undefined`.
      // The `for (const file of undefined)` below then threw
      // *after* the POST /owners had already succeeded — the
      // saga's catch arm fired `createItemError`, `goBack()` never
      // ran, and the modal stayed open over a freshly-created
      // row. Matches the `update` path which already defaulted to
      // `[]` for the same reason.
      document_templates = [],
      contract_terms,
      ...restAttributes
    } = attributes;

    const resp = await api.create({
      type: "owners",
      attributes: restAttributes,
    });
    const { id } = resp.data.data;

    for (const file of document_templates) {
      await uploadAttachment(
        file.name,
        current_user_id,
        null,
        file.localUrl,
        id,
        "Owner"
      );
    }

    await updateTerms({ id, terms, levels, contract_terms });

    return resp;
  },
  /**
   * updateOwner
   * @param id
   * @param attributes
   * @returns {Promise}
   */
  update: async (id, attributes) => {
    const {
      terms,
      levels,
      contract_template,
      current_user_id,
      document_templates = [],
      contract_terms,
      ...restAttributes
    } = attributes;

    const resp = await api.update({
      id,
      type: "owners",
      attributes: restAttributes,
    });

    if (document_templates.length) {
      for (const file of document_templates) {
        await uploadAttachment(
          file.name,
          current_user_id,
          null,
          file.localUrl,
          resp.data.data.id,
          "Owner"
        );
      }
    }

    await updateTerms({ id, terms, levels, contract_terms });

    return resp;
  },
  /**
   * deleteOwner
   * @returns {Promise<*>}
   */
  delete: (id) => api.destroy({ id, type: "owners" }),
  /**
   * get owner vessels
   * @param {String} id
   * @param queryOverride
   */
  getVessels: (id, queryOverride = {}) =>
    api.read(`owners/${id}/vessels`, {
      query: {
        page: { size: 999 },
        ...queryOverride,
      },
    }),

  getRecruiters: (id, queryOverride = {}) =>
    api.read(`owners/${id}/recruiters_list`, {
      query: {
        page: { size: 999 },
        ...queryOverride,
      },
    }),

  addRelation: async (id, relation, data) => {
    await api.apiRequest(`/api/owners/${id}/relationships/${relation}`, {
      method: "POST",
      body: JSON.stringify({ data }),
    });
  },

  deleteRelation: async (id, relation, data) => {
    await api.apiRequest(`/api/owners/${id}/relationships/${relation}`, {
      method: "DELETE",
      body: JSON.stringify({ data }),
    });
  },

  getScaleNationalities: (id) =>
    api.read(`owners/${id}/wage_scales_nationalities`, {
      query: { page: { size: 999 } },
    }),

  createNationalityScale: (id, attributes) =>
    api.update({
      id,
      type: "owners",
      action: "create_scale",
      attributes: attributes,
    }),

  ownerRoles: (id) =>
    api.read(`owners/${id}/roles`, { query: { page: { size: 999 } } }),

  getRRRoles: (id, queryOverride = {}) =>
    api.read(`owners/${id}/rr_roles_list`, {
      query: {
        page: { size: 999 },
        ...queryOverride,
      },
    }),

  getRetentionRate: (id, years = 5) =>
    api.read(`owners/${id}/retention_rate`, {
      query: { years },
    }),

  getRetentionRateByRange: (id, startDate, endDate) =>
    api.read(`owners/${id}/retention_rate_by_range`, {
      query: { start_date: startDate, end_date: endDate },
    }),

  // Admin-only .xlsx with the RR source table (pool history + per-year
  // AE/UW/RR% summary) — the data behind the RR chart.
  downloadRetentionRateReport: (id) =>
    downloadFile(`owners/${id}/retention_rate_report`, `retention-rate-${id}.xlsx`),
};
